import { useEffect, useState } from "react";

/**
 * BuilderTabs Component
 *
 * This component renders a set of tabs and their corresponding content. It allows the user
 * to switch between different tabs by clicking on the tab labels.
 *
 * @param {Array} tabs - An array of objects representing the tabs. Each object should have a
 *                       'label' property (string) for the tab label and a 'content' property
 *                       (React element) for the tab content.
 * @param {string} [tabsClass="sidebar_tabs"] - A string representing the CSS class applied to
 *                                              each tab label.
 * @param {string} [containerClass=""] - A string representing the CSS class applied to the
 *                                       container div of the tab labels.
 * @param {string} [contentClass=""] - A string representing the CSS class applied to the content
 *                             container.
 *
 * @example
 * const tabData = [
 *   {
 *     label: "Filters",
 *     content: <FiltersComponent />
 *   },
 *   {
 *     label: "Pre-filters",
 *     content: <PreFiltersComponent />
 *   }
 * ];
 *
 * <BuilderTabs tabs={tabData} />
 */
const BuilderTabs = ({
  tabs,
  tabsClass = "sidebar_tabs",
  containerClass = "",
  contentClass = "",
  onChange,
  contentStyle = {}
}) => {
  const [selectedTab, setSelectedTab] = useState(tabs[0]?.label);

  const handleTabClick = (label) => {
    setSelectedTab(label);
    if (onChange) {
      onChange(label);
    }
  };

  useEffect(() => {
    setSelectedTab(tabs[0]?.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  if (!tabs) return null;
  if (!Array.isArray(tabs)) return null;
  if (tabs.length === 0) return null;

  return (
    <>
      <div className={`sidebar_tab_buttons ${containerClass}`} style={{ position: "sticky", zIndex: 3, top: 0 }}>
        {tabs.map((tab, index) => (
          <div
            key={`${index}-${tab.label}`}
            className={`sidebar_tabs ${tabsClass} ${tab.label === selectedTab ? "active" : ""}`}
            onClick={() => handleTabClick(tab.label)}
          >
            {tab.label}
          </div>
        ))}
      </div>
      {tabs.map((tab) =>
        tab.label === selectedTab ? (
          <div key={tab.label} className={contentClass} style={contentStyle}>
            {tab.content}
          </div>
        ) : null
      )}
    </>
  );
};

export default BuilderTabs;
